import { Link as RouterLink } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Chip from '@mui/material/Chip'
import Link from '@mui/material/Link'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import type { Manifest, ManifestType } from '../../api/authoring'
import { offerLabel, typeTitle } from '../../model/manifest'
import { routes } from '../../model/routes'
import { orderedProperties, resolve, type Schema } from '../../model/schema'

/**
 * What a type is, as the registry declares it.
 *
 * **Every fact on this face was already being used and never shown.**
 * The palette in a placement picker is `contains`; the form in an editor
 * is `fields` and `intents`; the regions a tree draws are `slots`; the
 * picker beside them is `references`. An operator composing a page reads
 * all of it off the controls one at a time, and had nowhere to ask the
 * direct question — what does a Section hold, what goes in one, what can
 * I do to it.
 *
 * **It is a projection of the manifest and nothing else.** No type is
 * named, no shape is described here, and a type registered tomorrow —
 * or declared by this tenant this morning — gets this face with no
 * change. That is the same claim every other derived control in this
 * client makes, and this is the one screen where the claim is the
 * content.
 *
 * A tenant's own type says so and links to the definition that declares
 * it, which is an ordinary component with an ordinary history. A
 * compiled one says that too: what it holds is fixed by the build, and
 * an operator wondering why there is no edit button has been told.
 */
export function TypeFace({
  tenant,
  name,
  declared,
  manifest,
  definition,
}: {
  tenant: string
  /** The type's wire name, for a type the manifest does not carry. */
  name: string
  declared: ManifestType | undefined
  manifest: Manifest | undefined
  /**
   * The component whose document declares this type, where a tenant
   * declared it; `null` where nobody did, so the build registers it; and
   * absent while that is not yet known.
   *
   * Found by the caller, from the listing it already holds — this face
   * asks for no read of its own.
   */
  definition?: { id: string; label?: string } | null
}) {
  if (!declared) {
    return (
      <Alert severity="warning">
        This build&rsquo;s manifest does not declare <strong>{name}</strong>, so there is nothing to describe. A
        component of a type nobody registers still reads, and its fields are shown as they are stored.
      </Alert>
    )
  }

  const fields = declared.fields as Schema | undefined
  const properties = fields ? orderedProperties(fields, fields) : []

  return (
    <Stack spacing={3}>
      <Paper sx={{ p: 2.5 }}>
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Chip size="small" variant="outlined" label={declared.name} />
            {declared.role && <Chip size="small" variant="outlined" label={`a ${declared.role}`} />}
            <Chip
              size="small"
              variant="outlined"
              label={declared.inlineable ? 'may be defined in place' : 'always its own component'}
            />
            {declared.acceptsUpload && <Chip size="small" variant="outlined" label="holds a file" />}
          </Stack>

          {declared.prose?.description && (
            <Typography variant="body2" color="text.secondary">
              {declared.prose.description}
            </Typography>
          )}

          {/* Where the declaration lives, and whether it can be changed
              at all. A tenant's type is a component like any other, so
              it has a history, an audit and a revert; a compiled one has
              a build. Until that is known, it says neither. */}
          {definition ? (
            <Typography variant="body2">
              Declared by this tenant.{' '}
              <Link component={RouterLink} to={routes.component(tenant, definition.id)}>
                Open the definition
              </Link>{' '}
              to change what it holds.
            </Typography>
          ) : (
            definition === null && (
              <Typography variant="body2" color="text.secondary">
                Registered by the build. What it holds and what may be done to it are fixed for every tenant.
              </Typography>
            )
          )}
        </Stack>
      </Paper>

      <Section
        title="What it holds"
        caption="The document's own fields. A client draws a form from these and prefills it from what is stored; nothing writes a field except an operation below."
      >
        {properties.length === 0 ? (
          <Empty>This type holds no fields of its own.</Empty>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Field</TableCell>
                <TableCell>Shape</TableCell>
                <TableCell>Required</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {properties.map((property) => (
                <TableRow key={property.name}>
                  <TableCell>
                    <code>{property.name}</code>
                  </TableCell>
                  <TableCell>{shapeOf(property.schema, fields!)}</TableCell>
                  <TableCell>{property.required ? 'yes' : ''}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>

      <Section
        title="What may be done to one"
        caption="Every operation this type declares. A control exists for each of them somewhere in this client — as a form where the operation writes fields, and on the tree where it names a placement."
      >
        {declared.intents.length === 0 ? (
          <Empty>Nothing can edit a component of this type.</Empty>
        ) : (
          <Stack spacing={0.5}>
            {declared.intents.map((intent) => (
              <Stack key={intent.name} direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" useFlexGap>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {offerLabel(intent)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  <code>{intent.name}</code>
                </Typography>
              </Stack>
            ))}
          </Stack>
        )}
      </Section>

      <Section
        title="What goes in one"
        caption="The palette a placement control offers inside a component of this type. It is resolved: a type admitted by its role is listed here by name."
      >
        {declared.slots && declared.slots.length > 0 && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            <Typography variant="body2">Regions:</Typography>
            {declared.slots.map((slot) => (
              <Chip key={slot} size="small" label={slot} />
            ))}
          </Stack>
        )}

        {declared.contains.length === 0 ? (
          <Empty>Nothing is placed inside one — a component of this type is a leaf.</Empty>
        ) : (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {declared.contains.map((held) => (
              <Chip
                key={held}
                size="small"
                variant="outlined"
                component={RouterLink}
                clickable
                to={routes.components(tenant, held)}
                label={typeTitle(manifest, held)}
              />
            ))}
          </Stack>
        )}

        {declared.containsRoles && declared.containsRoles.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Declared as accepting {declared.containsRoles.map((role) => `any ${role}`).join(' or ')}, so a type
            registered later with that role joins the list above without this one changing.
          </Typography>
        )}
      </Section>

      {declared.references && declared.references.length > 0 && (
        <Section
          title="What one refers to"
          caption="Components a component of this type uses rather than renders inside itself. A reference has no position and no region — it is named, and the name is the role it plays."
        >
          <Stack spacing={0.5}>
            {declared.references.map((edge) => (
              <Stack key={edge.name} direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" useFlexGap>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {edge.name}
                </Typography>
                {edge.multiple && <Chip size="small" variant="outlined" label="many" />}
                {edge.authoredNames && <Chip size="small" variant="outlined" label="named by the declaration" />}
                <Typography variant="caption" color="text.secondary">
                  {edge.acceptsRoles?.length
                    ? edge.acceptsRoles.map((role) => `any ${role}`).join(' or ')
                    : edge.accepts.map((accepted) => typeTitle(manifest, accepted)).join(', ') || 'nothing yet'}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Section>
      )}
    </Stack>
  )
}

/**
 * One field's shape, in the words a schema can be read in.
 *
 * A vocabulary is spelled out because that is the fact an operator wants
 * — which values are legal — and a list says what it is a list of. No
 * property name and no type name appears in this function.
 */
function shapeOf(schema: Schema, root: Schema): string {
  const resolved = resolve(schema, root) ?? schema
  if (resolved.enum) return `one of ${resolved.enum.join(', ')}`
  if (resolved.type === 'array') {
    const items = resolve(resolved.items, root)
    return items ? `a list of ${shapeOf(items, root)}` : 'a list'
  }
  if (resolved.type === 'object') return resolved.properties ? 'a group of values' : 'named values'
  return resolved.type ?? 'a value'
}

function Section({ title, caption, children }: { title: string; caption: string; children: React.ReactNode }) {
  return (
    <Stack spacing={1}>
      <Stack spacing={0.25}>
        <Typography variant="h3">{title}</Typography>
        <Typography variant="caption" color="text.secondary">
          {caption}
        </Typography>
      </Stack>
      <Paper sx={{ p: 2 }}>{children}</Paper>
    </Stack>
  )
}

function Empty({ children }: { children: string }) {
  return (
    <Typography variant="body2" color="text.secondary">
      {children}
    </Typography>
  )
}
